import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Grid,
  GridItem,
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { t } from '@/lib/helpers'
import type { SubmitHandler } from 'react-hook-form'
import type { FilterParams } from '@/features/filters/types'

export interface UserFilterType {
  q?: FilterParams['q']
  has_connected?: FilterParams['has_connected']
}

export interface UserFilterFormProps {
  onSearch?: (filter: UserFilterType) => void
}

const CONNECTED_OPTIONS = [
  { value: 'all', label: 'views.admin.users.filters.all' },
  { value: 'true', label: 'views.admin.users.filters.connected' },
  { value: 'false', label: 'views.admin.users.filters.not_connected' },
] as const

const schema = yup.object().shape({
  q: yup
    .string()
    .nullable()
    .transform((v) => (v === '' ? undefined : v)),
  has_connected: yup
    .string()
    .oneOf(CONNECTED_OPTIONS.map(({ value }) => value)),
})

export const UserFilterForm = ({ onSearch }: UserFilterFormProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<UserFilterType>({
    defaultValues: {
      q: undefined,
      has_connected: 'all',
    },
    resolver: yupResolver(schema),
  })

  const onSubmit: SubmitHandler<UserFilterType> = (data) => {
    if (isSubmitting) return
    onSearch && onSearch(data)
  }

  return (
    <Box
      sx={{
        paddingRight: '40px',
        paddingLeft: '40px',
        marginTop: '-9px',
        marginBottom: '24px',
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid gap={4} templateColumns="repeat(12, 1fr)">
          <GridItem colSpan={5}>
            <FormControl>
              <FormLabel fontSize="0.875rem" fontWeight="400">
                {t('views.admin.users.filters.keyword')}
              </FormLabel>
              <Controller
                control={control}
                defaultValue=""
                name="q"
                render={(field) =>
                  <Input {...field}
                    placeholder={t('views.admin.users.filters.email_or_nickname_or_id')}
                  />
                }
              />
            </FormControl>
          </GridItem>

          <GridItem colSpan={5}>
            <FormControl>
              <FormLabel fontSize="0.875rem" fontWeight="400">
                {t('views.admin.users.filters.had_connected')}
              </FormLabel>
              <Controller
                control={control}
                name="has_connected"
                render={(field) => (
                  <Select {...field}>
                    {CONNECTED_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {t(label)}
                      </option>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </GridItem>

          <GridItem colSpan={2}>
            <FormControl>
              <FormLabel
                fontSize="0.875rem"
                fontWeight="400"
                sx={{ visibility: 'hidden' }}
              >
                {t('views.admin.users.filters.search')}
              </FormLabel>
              <Button isFullWidth type="submit">
                {t('views.admin.users.filters.search')}
              </Button>
            </FormControl>
          </GridItem>
        </Grid>
      </form>
    </Box>
  )
}
